import type { KeyboardEvent } from "react";

type ToothKind = "molar" | "premolar" | "canine" | "incisor";

const KIND_BY_POSITION: Record<number, ToothKind> = { 1: "incisor", 2: "incisor", 3: "canine", 4: "premolar", 5: "premolar", 6: "molar", 7: "molar", 8: "molar" };
const WIDTH: Record<number, number> = { 1: 21, 2: 16, 3: 17, 4: 17, 5: 16, 6: 24, 7: 22, 8: 20 };
const DEPTH: Record<ToothKind, number> = { molar: 24, premolar: 20, canine: 18, incisor: 13 };

const UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

const W = 400;
const CX = 200;
const RX = 150;
const RY = 172;
const SPAN = (84 * Math.PI) / 180;

export interface ToothPlacement {
  fdi: number;
  kind: ToothKind;
  x: number;
  y: number;
  angle: number;
  width: number;
  depth: number;
  jaw: "upper" | "lower";
}

function place(row: number[], jaw: "upper" | "lower"): ToothPlacement[] {
  const widths = row.map((t) => WIDTH[t % 10]!);
  const total = widths.reduce((a, b) => a + b, 0);
  let acc = 0;
  const cy = jaw === "upper" ? 214 : 226;
  return row.map((fdi, i) => {
    const w = widths[i]!;
    const mid = (acc + w / 2) / total;
    acc += w;
    const theta = (mid * 2 - 1) * SPAN;
    const x = CX + RX * Math.sin(theta);
    const y = jaw === "upper" ? cy - RY * Math.cos(theta) : cy + RY * Math.cos(theta);
    const angle = ((jaw === "upper" ? theta : Math.PI - theta) * 180) / Math.PI;
    const kind = KIND_BY_POSITION[fdi % 10]!;
    return { fdi, kind, x, y, angle, width: w * 1.32, depth: DEPTH[kind] * 1.3, jaw };
  });
}

export const ARCH_LAYOUT: ToothPlacement[] = [...place(UPPER, "upper"), ...place(LOWER, "lower")];

/** Occlusal outline centred on 0,0; +y points to the tongue side. */
function crownPath(kind: ToothKind, w: number, d: number) {
  const hw = w / 2;
  const hd = d / 2;
  if (kind === "incisor") {
    return `M ${-hw} ${-hd * 0.2} Q ${-hw} ${-hd} 0 ${-hd} Q ${hw} ${-hd} ${hw} ${-hd * 0.2} Q ${hw * 0.7} ${hd} 0 ${hd} Q ${-hw * 0.7} ${hd} ${-hw} ${-hd * 0.2} Z`;
  }
  if (kind === "canine") {
    return `M ${-hw} 0 Q ${-hw} ${-hd * 0.9} 0 ${-hd} Q ${hw} ${-hd * 0.9} ${hw} 0 Q ${hw * 0.8} ${hd} 0 ${hd} Q ${-hw * 0.8} ${hd} ${-hw} 0 Z`;
  }
  const r = kind === "molar" ? 0.42 : 0.5;
  return `M ${-hw} ${-hd * r} Q ${-hw} ${-hd} ${-hw * r} ${-hd} L ${hw * r} ${-hd} Q ${hw} ${-hd} ${hw} ${-hd * r} L ${hw} ${hd * r} Q ${hw} ${hd} ${hw * r} ${hd} L ${-hw * r} ${hd} Q ${-hw} ${hd} ${-hw} ${hd * r} Z`;
}

function groovePath(kind: ToothKind, w: number, d: number) {
  const hw = w / 2;
  const hd = d / 2;
  if (kind === "molar") return `M ${-hw * 0.5} 0 Q 0 ${-hd * 0.18} ${hw * 0.5} 0 M 0 ${-hd * 0.5} Q ${-hw * 0.12} 0 0 ${hd * 0.5}`;
  if (kind === "premolar") return `M ${-hw * 0.45} 0 Q 0 ${-hd * 0.2} ${hw * 0.45} 0`;
  if (kind === "canine") return `M 0 ${-hd * 0.55} L 0 ${hd * 0.1}`;
  return `M ${-hw * 0.6} ${-hd * 0.45} Q 0 ${-hd * 0.62} ${hw * 0.6} ${-hd * 0.45}`;
}

export function ToothGlyph({ kind, width, depth, className }: { kind: ToothKind; width: number; depth: number; className?: string }) {
  return (
    <g className={className}>
      <path className="tooth-crown" d={crownPath(kind, width, depth)} />
      <path className="tooth-groove" d={groovePath(kind, width, depth)} fill="none" />
    </g>
  );
}

export interface ArchProps {
  highlighted?: ReadonlySet<number>;
  selected?: ReadonlySet<number>;
  /** Toggles a tooth in or out of the selection. */
  onSelect?: (fdi: number) => void;
  onHover?: (fdi: number | null) => void;
  showNumbers?: boolean;
  className?: string;
  label?: string;
}

/** Occlusal view of both arches with FDI numbering. Interactive when onSelect is given. */
export function DentalArch({ highlighted, selected, onSelect, onHover, showNumbers = true, className, label = "Mapa dental" }: ArchProps) {
  const interactive = !!onSelect;
  const onKey = (e: KeyboardEvent, fdi: number) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect?.(fdi);
    }
  };
  return (
    <svg viewBox={`-6 -6 ${W + 12} 452`} className={className} role={interactive ? "group" : "img"} aria-label={label}>
      <line x1="40" x2="360" y1="220" y2="220" className="arch-midline" />
      <line x1="200" x2="200" y1="20" y2="420" className="arch-midline" />
      {ARCH_LAYOUT.map((t) => {
        const isOn = highlighted?.has(t.fdi) ?? false;
        const isSel = selected?.has(t.fdi) ?? false;
        const dx = t.x - CX;
        const dy = t.y - (t.jaw === "upper" ? 214 : 226);
        const len = Math.hypot(dx, dy) || 1;
        const nx = t.x + (dx / len) * 28;
        const ny = t.y + (dy / len) * 28;
        return (
          <g
            key={t.fdi}
            className="arch-tooth"
            data-on={isOn || undefined}
            data-selected={isSel || undefined}
            data-jaw={t.jaw}
            tabIndex={interactive ? 0 : undefined}
            role={interactive ? "button" : undefined}
            aria-pressed={interactive ? isSel : undefined}
            aria-label={interactive ? `Pieza ${t.fdi}` : undefined}
            onClick={interactive ? () => onSelect?.(t.fdi) : undefined}
            onKeyDown={interactive ? (e) => onKey(e, t.fdi) : undefined}
            onPointerEnter={onHover ? () => onHover(t.fdi) : undefined}
            onPointerLeave={onHover ? () => onHover(null) : undefined}
            onFocus={onHover ? () => onHover(t.fdi) : undefined}
            onBlur={onHover ? () => onHover(null) : undefined}
          >
            <g transform={`translate(${t.x.toFixed(2)} ${t.y.toFixed(2)}) rotate(${t.angle.toFixed(2)})`}>
              {interactive && <circle className="arch-hit" r={Math.max(t.width, t.depth) * 0.62} />}
              <ToothGlyph kind={t.kind} width={t.width} depth={t.depth} />
            </g>
            {showNumbers && (
              <text x={nx.toFixed(1)} y={ny.toFixed(1)} className="arch-number" textAnchor="middle" dominantBaseline="middle">
                {t.fdi}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
